import { formatRelative, statusTone } from "../api";
import { providerFromString, type ProviderId } from "../brands";
import { matchesAttribute } from "../../shared/tags";
import type { EvidenceStage } from "../components";
import { dependencySearchText, needsDependency, needsLine } from "./loopDependencies";
import { providerName } from "./loopPlacements";
import { sharedByLine } from "./sharingBulk";
import type { LoopModel } from "./LoopsPage";

/**
 * Pure logic behind the Loops list: the segmented control and its counts, the search and facet
 * filters, the facts each ledger row shows, and the persisted Ledger / Cards choice. LoopsPage
 * renders these; LoopDetailPage reaches the loop predicates through LoopsPage's re-exports.
 */

export type LoopFilter = "all" | "live" | "failed" | "silent" | "partial" | "evaluable";
export type PresentedStatus = "succeeded" | "failed" | "running" | "cancelled" | "unknown";
export type LoopsView = "ledger" | "cards";

/** The segments of the primary filter, in display order; the description is the segment's hover title. */
export const LOOP_FILTERS: ReadonlyArray<{ value: LoopFilter; label: string; description: string }> = [
  { value: "all", label: "All", description: "The full registry" },
  { value: "live", label: "In motion", description: "Latest run is running or queued" },
  { value: "failed", label: "Failed latest", description: "Latest run failed" },
  { value: "silent", label: "Silent", description: "No runs reported yet" },
  { value: "partial", label: "Outcome gaps", description: "Runs still open, cancelled, or unresolved" },
  { value: "evaluable", label: "Evaluable", description: "At least three resolved outcomes" },
];

const LIVE_STATUSES = new Set(["running", "queued"]);
const FAILED_STATUSES = new Set(["failed", "error"]);
/** Reliability is withheld until this many outcomes have resolved (DESIGN.md §6). */
const RELIABILITY_GATE = 3;

function normalizedStatus(value: string | null | undefined): string {
  return value?.trim().toLowerCase() ?? "";
}

export function presentedStatus(value: string | null | undefined): PresentedStatus {
  const normalized = normalizedStatus(value);
  if (normalized === "succeeded" || normalized === "success" || normalized === "completed") return "succeeded";
  if (normalized === "failed" || normalized === "error") return "failed";
  if (normalized === "running" || normalized === "queued") return "running";
  if (normalized === "cancelled" || normalized === "canceled") return "cancelled";
  return "unknown";
}

export function isLive(loop: Pick<LoopModel, "performance">): boolean {
  return LIVE_STATUSES.has(normalizedStatus(loop.performance.lastRunStatus));
}

export function hasFailedLatest(loop: Pick<LoopModel, "performance">): boolean {
  return FAILED_STATUSES.has(normalizedStatus(loop.performance.lastRunStatus));
}

export function matchesFilter(loop: Pick<LoopModel, "performance">, filter: LoopFilter): boolean {
  if (filter === "live") return isLive(loop);
  if (filter === "failed") return hasFailedLatest(loop);
  if (filter === "silent") return loop.performance.runs === 0;
  if (filter === "partial") return loop.performance.runs > loop.performance.resolvedRuns;
  if (filter === "evaluable") return loop.performance.resolvedRuns >= RELIABILITY_GATE;
  return true;
}

/** How many loops each segment would show; counted over the whole catalog, never the filtered view. */
export function segmentCounts(loops: Array<Pick<LoopModel, "performance">>): Record<LoopFilter, number> {
  const counts: Record<LoopFilter, number> = { all: 0, live: 0, failed: 0, silent: 0, partial: 0, evaluable: 0 };
  for (const loop of loops) {
    for (const option of LOOP_FILTERS) if (matchesFilter(loop, option.value)) counts[option.value] += 1;
  }
  return counts;
}

export function evidenceFor(loop: Pick<LoopModel, "performance" | "discovery">): { stage: EvidenceStage; label: string; detail: string } {
  if (loop.performance.resolvedRuns >= RELIABILITY_GATE) return { stage: "evaluable", label: "Enough evidence", detail: `${loop.performance.resolvedRuns} resolved` };
  if (loop.performance.runs > 0) return { stage: "reporting", label: "Reporting", detail: loop.performance.resolvedRuns ? `${loop.performance.resolvedRuns} resolved · early` : "Outcomes unresolved" };
  if (loop.discovery) return { stage: loop.discovery.confidence === "inferred" ? "inferred" : "connected", label: "Connected", detail: loop.discovery.confidence || "Confidence unrated" };
  return { stage: "cataloged", label: "Cataloged", detail: "No runs reported yet" };
}

/** Where a loop came from: who shared it (and by which rule) in a group, otherwise the source it was cataloged from. */
export function originFor(loop: Pick<LoopModel, "visibility" | "owner" | "discovery" | "team">): string {
  if (loop.visibility.mode === "shared") return sharedByLine({ ownerName: loop.owner?.name, originName: loop.visibility.originOrganizationName, rule: loop.visibility.rule ?? null });
  return loop.discovery?.source.name || loop.discovery?.source.provider || loop.team?.name || "Declared manually";
}

/** The mark a loop wears: the agent it runs on now when that is known, otherwise the source it was cataloged from. */
export function markProviderFor(loop: Pick<LoopModel, "runsOn" | "discovery">): string | null | undefined {
  return loop.runsOn?.provider ?? loop.discovery?.source.provider;
}

/** The hero subtitle names the current home first when it differs from the catalog origin. */
export function homeLineFor(loop: LoopModel): string {
  const origin = originFor(loop);
  const home = providerFromString(loop.runsOn?.provider);
  const source = providerFromString(loop.discovery?.source.provider);
  if (!home || home === source || loop.visibility.mode === "shared") return `${origin} · ${loop.cadence}`;
  return `Runs on ${providerName(home)} · cataloged from ${origin} · ${loop.cadence}`;
}

/** The agent a loop runs on, as the Runs on facet counts it: the reported home, else the last run's provider. */
export function providerOf(loop: Pick<LoopModel, "performance" | "runsOn">): ProviderId | null {
  return providerFromString(loop.runsOn?.provider ?? loop.performance.lastRunProvider);
}

/** Whether a loop carries this category tag, compared the way the Tags facet counted it. */
function taggedWith(loop: Pick<LoopModel, "tags" | "cadence">, tag: string): boolean {
  return matchesAttribute({ tags: loop.tags, sourceId: null, kind: null, cadence: loop.cadence }, "tag", tag);
}

export type LedgerCriteria = { query: string; filter: LoopFilter; dependency: string | null; tag: string | null; provider: ProviderId | null };

export const EMPTY_CRITERIA: LedgerCriteria = { query: "", filter: "all", dependency: null, tag: null, provider: null };

function searchText(loop: LoopModel): string {
  return `${loop.name} ${loop.description ?? ""} ${loop.team?.name ?? ""} ${originFor(loop)} ${loop.discovery?.confidence ?? ""} ${dependencySearchText(loop)} ${loop.tags.join(" ")}`.toLowerCase();
}

/** The loops the list shows: every loop that matches the search, the segment, and each active facet. */
export function filterLoops(loops: LoopModel[], criteria: LedgerCriteria): LoopModel[] {
  const needle = criteria.query.trim().toLowerCase();
  return loops.filter((loop) =>
    (!needle || searchText(loop).includes(needle))
    && matchesFilter(loop, criteria.filter)
    && (!criteria.dependency || needsDependency(loop, criteria.dependency))
    && (!criteria.tag || taggedWith(loop, criteria.tag))
    && (!criteria.provider || providerOf(loop) === criteria.provider));
}

/** Whether a facet (runs on, needs, or tag) narrows the view; the segment and search do not count. */
export function hasFacetFilter(criteria: LedgerCriteria): boolean {
  return Boolean(criteria.dependency || criteria.tag || criteria.provider);
}

/** " · on Claude · needs X · tagged Y" after the Filters label; empty when no facet is active. */
export function facetSuffix(criteria: LedgerCriteria): string {
  const parts: string[] = [];
  if (criteria.provider) parts.push(`on ${providerName(criteria.provider)}`);
  if (criteria.dependency) parts.push(`needs ${criteria.dependency}`);
  if (criteria.tag) parts.push(`tagged ${criteria.tag}`);
  return parts.map((part) => ` · ${part}`).join("");
}

/** Reliability with its resolved sample, or an em dash naming the gate. Never a percentage without its n. */
export function reliabilityCell(performance: Pick<LoopModel["performance"], "resolvedRuns" | "successRate">): { value: string; detail: string; withheld: boolean } {
  if (performance.resolvedRuns >= RELIABILITY_GATE && performance.successRate != null) {
    return { value: `${Math.round(performance.successRate * 100)}%`, detail: `${performance.resolvedRuns} resolved`, withheld: false };
  }
  return { value: "—", detail: `needs ${RELIABILITY_GATE} resolved`, withheld: true };
}

/** The Runs on cell: the agent's name, or an em dash when no agent has reported the loop. */
export function runsOnCell(loop: Pick<LoopModel, "performance" | "runsOn">): { text: string; provider: ProviderId | null } {
  const provider = providerOf(loop);
  return { text: provider ? providerName(provider) : "—", provider };
}

function formatAbsolute(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

/** The Last signal cell: relative time with the absolute moment as its title, or an em dash that says nothing was reported. */
export function lastSignalCell(lastRunAt: string | null): { text: string; title: string } {
  if (!lastRunAt) return { text: "—", title: "No runs reported yet" };
  return { text: formatRelative(lastRunAt), title: `Last signal ${formatAbsolute(lastRunAt)}` };
}

/** The mobile row's second line restates the columns it hides: cadence, then the last signal. */
export function secondLine(loop: Pick<LoopModel, "cadence" | "performance">): string {
  const signal = lastSignalCell(loop.performance.lastRunAt);
  return `${loop.cadence} · ${signal.text === "—" ? "no runs reported yet" : signal.text}`;
}

/** The status dot's tone and the name assistive technology reads for it; a dot never carries status by colour alone. */
export function statusCell(loop: Pick<LoopModel, "performance">): { tone: ReturnType<typeof statusTone>; label: string } {
  const status = presentedStatus(loop.performance.lastRunStatus);
  if (status === "unknown" && loop.performance.runs === 0) return { tone: "quiet", label: "No runs reported yet" };
  return { tone: statusTone(status), label: `Latest run ${status}` };
}

/** The row's hover title: the origin when shared, the description, and the Needs line; undefined when there is nothing to add. */
export function rowTitle(loop: LoopModel): string | undefined {
  const lines = [loop.visibility.mode === "shared" ? originFor(loop) : null, loop.description, needsLine(loop.dependencies)].filter((line): line is string => Boolean(line));
  return lines.length ? lines.join("\n") : undefined;
}

export const LOOPS_VIEW_KEY = "lg.loopsView";

type ViewStorage = Pick<Storage, "getItem" | "setItem">;

/** The persisted list view; the ledger unless the viewer chose cards. Storage that throws (private browsing) reads as the default. */
export function readLoopsView(storage: ViewStorage): LoopsView {
  try {
    return storage.getItem(LOOPS_VIEW_KEY) === "cards" ? "cards" : "ledger";
  } catch {
    return "ledger";
  }
}

export function writeLoopsView(storage: ViewStorage, view: LoopsView): void {
  try {
    storage.setItem(LOOPS_VIEW_KEY, view);
  } catch {
    // Private browsing or a full store: the choice lasts for the session only.
  }
}
